import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaSearch,
  FaTimes,
  FaEdit,
  FaWeightHanging,
  FaTag,
  FaBoxOpen,
  FaBox,
} from "react-icons/fa";
import { motion } from "framer-motion";
import toast, { Toaster } from "react-hot-toast";
import { CheckCircle, AlertCircle } from "lucide-react";
import SummaryApi from "../common";
import displayINRCurrency from "../helpers/displayCurrency";

// Rich Text Editor Imports
import ReactQuill from "react-quill-new"; // New
import "react-quill/dist/quill.snow.css";

const IMAGE_BASE_URL = SummaryApi?.productImage?.url
  ? SummaryApi.productImage.url.replace("/api/uploads", "")
  : "http://192.168.1.144:3001";

const AllProducts = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(SummaryApi.getAllProduct.url, {
        method: SummaryApi.getAllProduct.method,
        headers: { "Content-Type": "application/json" },
      });
      const result = await res.json();

      if (result.status === true && Array.isArray(result.data)) {
        setProducts(result.data);
        setFilteredProducts(result.data);
      } else {
        toast.error(result.message || "Failed to load products");
      }
    } catch (err) {
      console.error(err);
      toast.error("Network error while loading products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const q = search.toLowerCase().trim();
    if (!q) {
      setFilteredProducts(products);
      return;
    }
    const filtered = products.filter(
      (p) =>
        (p.product_title || "").toLowerCase().includes(q) ||
        (p.product_code || "").toLowerCase().includes(q),
    );
    setFilteredProducts(filtered);
  }, [search, products]);

  const openModal = (product = null) => {
    setEditProduct(product);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditProduct(null);
  };

  const showSuccess = (title, msg) => {
    toast.custom(
      (t) => (
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="max-w-md w-full bg-white shadow-2xl rounded-3xl border-b-4 border-pink-600 overflow-hidden"
        >
          <div className="p-5 flex items-start gap-4">
            <div className="h-12 w-12 rounded-2xl bg-pink-100 flex items-center justify-center text-pink-600">
              <CheckCircle size={28} />
            </div>
            <div>
              <p className="font-bold text-gray-900">{title}</p>
              <p className="text-sm text-gray-600 mt-1">{msg}</p>
            </div>
          </div>
        </motion.div>
      ),
      { duration: 2000, position: "top-center", id: "product-success-toast" },
    );
  };

  // ────────────────────────────────────────────────
  //                Product Card (Grid View)
  // ────────────────────────────────────────────────
  const ProductCard = ({ product }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -6, transition: { duration: 0.25 } }}
      className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 group flex flex-col h-full"
    >
      <div className="relative h-48 bg-gray-50 flex items-center justify-center p-4">
        <img
          src={
            product.product_image1
              ? `${IMAGE_BASE_URL}/${product.product_image1}`
              : "https://via.placeholder.com/400x300?text=No+Image"
          }
          alt={product.product_title}
          className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500"
        />
        {/* ACTIVE - top left */}
<div className="absolute top-3 left-3">
  <span
    className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${
      product.is_active === 1
        ? "bg-green-100 text-green-700 border-green-200"
        : "bg-red-100 text-red-700 border-red-200"
    }`}
  >
    {product.is_active === 1 ? "ACTIVE" : "INACTIVE"}
  </span>
</div>

{/* STOCK - top right */}
<div className="absolute top-3 right-3">
  <span
    className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${
      product.is_stock_available === 1
        ? "bg-blue-100 text-blue-700 border-blue-200"
        : "bg-orange-100 text-orange-700 border-orange-200"
    }`}
  >
    {product.is_stock_available === 1 ? "IN STOCK" : "OUT OF STOCK"}
  </span>
</div>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex justify-between mb-3">
          <div className="mb-3">
            <h3 className="font-bold text-base text-gray-900 line-clamp-1 mb-0.5">
              {product.product_title}
            </h3>
            <p className="text-[11px] text-gray-400 font-medium tracking-tight">
              Code: {product.product_code || "---"}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[14px] font-bold text-pink-600">
              ₹{product.selling_price || "0"}
            </p>
            <p className="text-[10px] text-gray-400 line-through">
              ₹{product.actual_price || "0"}
            </p>
          </div>
        </div>

        <div className="flex-1">
          <p className="text-[10px] font-bold text-gray-400 uppercase mb-2 tracking-wider">
            Available Units ({product.units?.length || 0})
          </p>
          <div className="grid grid-cols-1 gap-2 mb-4">
            {product.units?.slice(0, 2).map((unit) => (
              <div
                key={unit.product_unit_id}
                className="flex items-center justify-between bg-pink-50/50 border border-pink-100 rounded-lg px-3 py-2"
              >
                <span className="text-[11px] font-bold text-gray-700">
                  Pack {unit.pack_size}
                </span>
                <span className="text-xs font-black text-pink-600">
                  ₹{unit.price}
                </span>
              </div>
            ))}
            {product.units?.length > 2 && (
              <p className="text-[10px] text-center text-gray-400">
                +{product.units.length - 2} more variants
              </p>
            )}
          </div>
        </div>

        <div className="mt-auto pt-4 border-t border-gray-50">
          <button
            onClick={() => openModal(product)}
            className="w-full py-2.5 bg-white border border-pink-200 text-pink-600 font-bold rounded-xl hover:bg-pink-600 hover:text-white transition-all shadow-sm flex items-center justify-center gap-2 text-sm"
          >
            <FaEdit /> Edit Details
          </button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-purple-50 pb-16">
      {/* <Toaster position="top-center" /> */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
          <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
            All Products
          </h1>
          <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
            <div className="relative flex-1 md:w-80">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="search"
                placeholder="Search title, type, code..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-12 pr-5 py-3.5 bg-white border border-gray-200 rounded-full shadow focus:ring-4 focus:ring-pink-200 outline-none"
              />
            </div>
            <button
              onClick={() => openModal(null)}
              className="flex items-center justify-center gap-2 px-7 py-3.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold rounded-full shadow-lg hover:from-pink-600 transition whitespace-nowrap"
            >
              <FaPlus /> Add Product
            </button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="h-80 bg-white/50 animate-pulse rounded-2xl"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
            {filteredProducts.map((product) => (
              <ProductCard key={product._id} product={product} />
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <ProductModal
          product={editProduct}
          onClose={closeModal}
          onSuccess={() => {
            fetchProducts();
            showSuccess(
              "Success!",
              editProduct ? "Product updated" : "Product created",
            );
          }}
        />
      )}
    </div>
  );
};

// ────────────────────────────────────────────────
//                 Product Modal
// ────────────────────────────────────────────────
const ProductModal = ({ product, onClose, onSuccess }) => {
  const isEdit = !!product;
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(
    isEdit
      ? {
          ...product,
          actual_price: product.actual_price || "",
          discount: product.discount || 0,
          selling_price: product.selling_price || 0,
          is_active: product.is_active === 1,
          is_stock_available: product.is_stock_available === 1,
          about_product: product.about_product || "",
        }
      : {
          product_title: "",
          subtitle: "",
          actual_price: "",
          discount: 0,
          selling_price: 0,
          about_product: "",
          product_size: "",
          is_active: true,
          is_stock_available: true,
          product_image1: null,
          product_image2: null,
          product_image3: null,
          product_image4: null,
          product_image5: null,
        },
  );

  const [previews, setPreviews] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "file" && files?.[0]) {
      const file = files[0];
      setForm((prev) => ({ ...prev, [name]: file }));
      const reader = new FileReader();
      reader.onload = () =>
        setPreviews((p) => ({ ...p, [name]: reader.result }));
      reader.readAsDataURL(file);
    } else if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => {
        const updatedForm = { ...prev, [name]: value };
        const actual =
          name === "actual_price" ? Number(value) : Number(prev.actual_price);
        const selling =
          name === "selling_price" ? Number(value) : Number(prev.selling_price);
        if (actual > 0) {
          const calcDiscount = ((actual - selling) / actual) * 100;
          updatedForm.discount = Math.max(
            0,
            parseFloat(calcDiscount.toFixed(2)),
          );
        } else {
          updatedForm.discount = 0;
        }
        return updatedForm;
      });
    }
  };

  // Custom Handler for Rich Text
  const handleEditorChange = (content) => {
    setForm((prev) => ({ ...prev, about_product: content }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const fd = new FormData();
    if (isEdit && product._id) fd.append("_id", product._id);

    Object.entries(form).forEach(([key, val]) => {
      if (key === "product_code" || key === "description") return; // Skip description as requested
      if (val === null || val === undefined) return;

      if (val instanceof File) {
        fd.append(key, val);
      } else if (key === "is_active" || key === "is_stock_available") {
        fd.append(key, val ? "1" : "0");
      } else if (["actual_price", "discount", "selling_price"].includes(key)) {
        fd.append(key, Number(val) || 0);
      } else {
        fd.append(key, val);
      }
    });

    const url = isEdit
      ? SummaryApi.UpdateProduct.url
      : SummaryApi.CreateProduct.url;
    const method = isEdit
      ? SummaryApi.UpdateProduct.method
      : SummaryApi.CreateProduct.method;

    try {
      const res = await fetch(url, { method, body: fd });
      const data = await res.json();
      if (data.status === true) {
        onSuccess?.();
        onClose();
      } else toast.error(data.message || "Operation failed");
    } catch (err) {
      toast.error("Network error.");
    } finally {
      setSubmitting(false);
    }
  };

  const getPreview = (key) =>
    previews[key] ||
    (form[key] && typeof form[key] === "string"
      ? `${IMAGE_BASE_URL}/${form[key]}`
      : null);

  // Quill Modules Configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "clean"],
    ],
  };

  return (
    <div className="fixed inset-0 bg-black/65 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.94, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b z-20 px-7 py-5 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEdit ? "Edit Product" : "Create New Product"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <FaTimes className="text-2xl text-gray-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-7 space-y-7">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-1">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Product Title *
              </label>
              <input
                required
                name="product_title"
                value={form.product_title}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-200 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Subtitle
              </label>
              <input
                name="subtitle"
                value={form.subtitle || ""}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-200 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Product Size
              </label>
              <input
                name="product_size"
                value={form.product_size || ""}
                onChange={handleChange}
                placeholder="eg: xl, 2xl"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-200 outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Actual Price (₹)
              </label>
              <input
                type="number"
                name="actual_price"
                value={form.actual_price}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-200 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Selling Price (₹)
              </label>
              <input
                type="number"
                name="selling_price"
                value={form.selling_price}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-200 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Discount (%)
              </label>
              <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-pink-600 font-bold">
                {form.discount}%
              </div>
            </div>
          </div>

          {/* RICH TEXT EDITOR FOR ABOUT PRODUCT */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              About Product *
            </label>
            <div className="rounded-xl overflow-hidden border border-gray-200">
              <ReactQuill
                theme="snow"
                value={form.about_product}
                onChange={handleEditorChange}
                modules={modules}
                className="bg-white min-h-[200px]"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3">
              Product Images
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {[1, 2, 3, 4, 5].map((n) => (
                <div
                  key={n}
                  className="relative aspect-square rounded-xl border-2 border-dashed border-gray-300 overflow-hidden bg-gray-50 flex items-center justify-center group"
                >
                  {getPreview(`product_image${n}`) ? (
                    <img
                      src={getPreview(`product_image${n}`)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-gray-400 text-xs">Image {n}</span>
                  )}
                  <input
                    type="file"
                    name={`product_image${n}`}
                    accept="image/*"
                    onChange={handleChange}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-8 bg-gray-50 p-5 rounded-2xl">
            {/* Status Toggles */}
            <label className="flex items-center cursor-pointer gap-3 select-none">
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
                className="sr-only"
              />
              <div
                className={`w-11 h-6 rounded-full transition-colors ${form.is_active ? "bg-green-500" : "bg-gray-300"}`}
              >
                <div
                  className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform ${form.is_active ? "translate-x-6" : "translate-x-1"} mt-1`}
                />
              </div>
              <span className="font-bold text-sm text-gray-700">
                Active Status
              </span>
            </label>

            <label className="flex items-center cursor-pointer gap-3 select-none">
              <input
                type="checkbox"
                name="is_stock_available"
                checked={form.is_stock_available}
                onChange={handleChange}
                className="sr-only"
              />
              <div
                className={`w-11 h-6 rounded-full transition-colors ${form.is_stock_available ? "bg-blue-500" : "bg-gray-300"}`}
              >
                <div
                  className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform ${form.is_stock_available ? "translate-x-6" : "translate-x-1"} mt-1`}
                />
              </div>
              <span className="font-bold text-sm text-gray-700">
                Stock Available
              </span>
            </label>
          </div>

          <div className="flex justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-7 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              disabled={submitting}
              type="submit"
              className="px-8 py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold rounded-xl hover:opacity-90 transition shadow-md disabled:opacity-50"
            >
              {submitting
                ? "Processing..."
                : isEdit
                  ? "Update Product"
                  : "Create Product"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AllProducts;
